/** 다시 만들기(`A-25`)가 실패했을 때 시트가 하는 말. **돈을 말하는 표다.**
 *
 * ## 왜 `failure-messages.ts` 옆에 따로 있나
 *
 * **저 표는 일부러 돈 얘기를 하지 않는다.** 그 파일 머리와 `TOTAL_FAILURE` 주석이 근거를
 * 적어 뒀다(돈 낱말이 나오는 자리에는 `실행 방식 미정` 배지가 붙어야 하는데 평문 문자열에는
 * 못 단다 · `되돌립니다` 는 판 복원이 쓰는 말이다). 그래서 저쪽 `detail` 은 "다시 시도해
 * 주세요"로 끝나는데, **이 시트에서 그 말은 돈이 드는 동작이다**(PRD `7.6.8` · `7.13` D-62).
 * 저 표로 이 시트를 덮으면 셀러가 장당 과금을 "다시 시도"로 읽는다.
 *
 * **그래서 덮지 않고 나란히 둔다.** 두 파일이 같은 자리에 있어야 다음 사람이
 * **"이 표는 돈을 말하고 저 표는 말하지 않는다"** 를 본다. 저 표에서 가져오는 것은
 * 다섯째 갈래의 `short` 와 `retryable` 둘뿐이고, ⛔ **`MESSAGES` 에 돈 문장을 섞지 마라** —
 * 섞는 순간 이 구분이 사라진다.
 *
 * ## 이 표가 가르는 것 — **부르기 전과 부른 뒤**
 *
 * 서버가 상태 코드로 그 선을 긋는다(`apps/api/src/routes/jobs.ts` 의 다시 만들기 라우트).
 * **`409` 는 provider 를 부르기 전이고 `502` 는 부른 뒤다.** `금액은 들지 않았습니다` 를
 * 말할 수 있는 갈래가 앞이고, **뒤에 같은 말을 쓰면 거짓이다.**
 * `SOURCE_MISSING` 이 양쪽에 있는 것은 의도다 — 호출을 잡기 전에 걸리면 409, 잡은 뒤
 * 경합으로 사라진 것을 만나면 502 다.
 *
 * ## 이 표의 `금액` 은 전부 **셀러 청구가**다. 우리 원가가 아니다
 *
 * 장당 ₩500(`CUTOUT_UNIT_PRICE_KRW`)이고, provider 에 나가는 ₩27 은 여기 한 글자도
 * 나오지 않는다(`cost.ts` 가 이름으로 그 둘을 갈라 뒀다). **부른 뒤 갈래에서 우리 원가는
 * 실제로 나갔다.** 그 사실은 `providerCallCount` 에 남고(호출 직전에 오른다) 셀러에게는
 * 말하지 않는다.
 *
 * ⛔ **결제가 아직 붙지 않아, 지금은 어느 갈래에서도 셀러 카드에서 돈이 빠지지 않는다**
 * (`A-26` 미구현). ①~④ 의 금액 문장은 **결제가 붙었을 때 어떻게 되는가를 규칙으로**
 * 말하는 것이라 그 상태에서도 참이다. ⑤ 만 지금 상태를 직접 적는데, **그 갈래는 처리가
 * 미정이라 규칙으로 말할 수가 없다**(아래 `FAILED_AFTER_CALL` 주석). **결제가 붙는 날
 * 다시 읽어야 할 문장이 ⑤ 다.**
 *
 * ## 여기 없는 두 상태 (실패가 아니라 진행 상태다)
 *
 * `A-25` 시트에는 이 표를 쓰지 않는 배너가 둘 더 있다. 실패가 아니라서 이 표에 넣지 않는다.
 * 대기 줄은 **화면이 들고 있고**(`apps/mobile/src/features/job/retry-sheet.tsx`), 만드는 중
 * 줄은 2026-10-13 부터 이 파일 아래 `REVIVE_SHEET_COPY.pendingBanner` 가 들고 있다(못 만든 장
 * 시트·패널과 같은 글자라 한 벌로 올렸다).
 *
 * | 언제 | 아이콘 | 문구 |
 * |---|---|---|
 * | 대기 | `₩` | `결제가 아직 붙지 않아 지금은 청구되지 않습니다. 붙으면 이 금액이 나갑니다. 부가세가 포함된 금액입니다.` (셋째 문장은 `VAT_INCLUDED_NOTICE`) |
 * | 만드는 중 | `↓` | `다시 만들고 있습니다. 닫아도 계속 만듭니다.` |
 *
 * **아이콘이 갈린 자리이기도 하다.** 실패 다섯은 전부 `!` 이고 위 둘만 `₩`·`↓` 다.
 *
 * ⚠️ **위 두 줄은 코드의 복사본이다.** 정본은 코드(대기 줄은 `retry-sheet.tsx`, 만드는 중 줄은
 * 아래 `REVIVE_SHEET_COPY`)와 PRD `7.6.8` 이고,
 * 여기 옮겨 적은 것은 **이 파일이 덮지 않는 상태가 무엇인지**를 보이기 위해서다. 그래서
 * **화면이 바뀌면 이 줄도 같은 회차에 바뀌어야 한다.** 안 바뀌면 다음 사람이 화면에 없는
 * 문장을 정본으로 읽는다. 실제로 두 줄 다 그렇게 갈렸었고 2026-09-18 에 맞췄다.
 *
 * | 걷힌 판 | 언제·왜 |
 * |---|---|
 * | 만드는 중 가운데의 `한 장에 3초쯤 걸립니다` | 2026-09-18. **잰 적이 없는 숫자였다.** Photoroom 상용 API 시절 값이고 지금 공급자(CPU 위 ONNX)로 재지 않았다. `A-24`·`A-24b`·`W-24` 에서 같은 회차에 함께 걷었다(`7.6.7`·`7.6.8`) |
 * | 대기 줄이 두 문장이던 판 | 2026-09-13 에 부가세 단서가 셋째 문장으로 붙었는데 **이 표만 따라오지 않았다.** 뒤늦게 맞춘 것이라 새 결정이 아니다(`7.6.8`) |
 * | 대기 줄 셋째 문장 `부가세가 포함된 값인지는 아직 정해지지 않았습니다.` | 2026-09-14 `13.1` O-44 로 「부가세 포함」이 확정됐는데 화면에 미정 문장이 남아 있었다. 2026-10-03 에 화면과 이 표를 같은 회차에 `VAT_INCLUDED_NOTICE` 로 맞췄다 |
 *
 * ⛔ **걷은 자리에 새 숫자를 끼우지 마라.** 콜드 스타트 9.0초도 `/v1/compose` 왕복
 * 0.55~0.76초도 **장당 시간이 아니다.** 되돌릴 조건은 `/v1/cutout` 실측 하나이고 `7.13`
 * **D-78** 로 열려 있다. 같은 실측이 `processing-budget.ts` 의
 * `CUTOUT_PER_IMAGE_ESTIMATE_MS` 도 함께 닫는다.
 *
 * 문구 정본은 `design-manager` 다(2026-09-13 확정 · 2026-09-18 개정). **글자를 여기서
 * 정하지 않는다.** 정본이 바뀌면 그 판본을 그대로 옮겨 적기만 한다.
 */

import type { JobFailureCode } from "./domain";
import { failureMessage } from "./failure-messages";
import {
  IMAGE_NOT_REQUEUED_CODE,
  IMAGE_NOT_REQUEUED_TEXT,
  RESULT_MAKE_EXPIRED_COPY,
  RESULT_RECEIVE_EXPIRED_CODE,
} from "./result-retention-copy";

/** 원본이 파기된 작업을 서버가 거절할 때 싣는 코드(`apps/api` 의 `jobs.ts` · `compositions.ts`).
 *  `domain.ts` 의 `JobFailureCode` 와 같은 글자다. */
const JOB_DISCARDED_CODE = "JOB_DISCARDED";

/** 누른 요청이 **원본 없는 작업** 409 로 돌아왔는가. `remakeOutcomeMessage` 가 이 거절을 따로
 *  그리는 조건과 같다(502 에 같은 코드가 실려도 아니다).
 *
 * 화면은 이 값으로 **본문과 금액 줄을 걷는다**(받기 기간 409 의 `isResultReceiveExpired` 와 같은
 * 규칙). 버튼이 꺼졌는데 「₩500이 또 듭니다」 · `지금 결제할 금액 ₩500` 을 남기면 일어나지 않을
 * 일을 약속한다. */
export function isRemakeRejectedDiscarded(error: RemakeErrorLike | null | undefined): boolean {
  return !!error && error.status === 409 && error.code === JOB_DISCARDED_CODE;
}

/** 다시 만들기가 실패하는 다섯 갈래. **가르는 기준은 원인이 아니라 "돈이 나갔는가"다.**
 *
 * 원인으로 가르면 갈래가 서버 코드 수만큼 늘고, 셀러가 알아야 하는 것은 그게 아니다. */
export type RemakeOutcome =
  /** ① 이미 다시 만들어졌다. `409 STALE_CALL_COUNT`. **돈은 한 번만 나갔다.**
   *  다른 기기가 먼저 눌렀거나 같은 요청이 두 번 닿았다. 화면이 할 일은 재조회 하나다. */
  | "ALREADY_REMADE"
  /** ② 이 작업의 다른 장을 만드는 중. `409 ALREADY_PROCESSING`. 안 나갔다. */
  | "JOB_BUSY"
  /** ③ 부르기 전에 막혔다. `409`(나머지) · `503` · `404` · `400`. 안 나갔다. */
  | "BLOCKED_BEFORE_CALL"
  /** ④ 답이 없었다. 네트워크·타임아웃(`status === 0`)과 **모르는 응답 전부.**
   *  나갔는지 **모른다** — 이 갈래만 돈에 대해 단정하지 않는다. */
  | "NO_ANSWER"
  /** ⑤ 불러 놓고 실패했다. `502`. **나갔다.** 갖고 있던 결과는 그대로다. */
  | "FAILED_AFTER_CALL";

/** 판정에 필요한 것만 받는다. `apps/mobile/src/lib/api.ts` 의 `ApiError` 가 구조로 맞는다 —
 *  그 클래스를 여기서 참조하면 웹이 못 쓰고, 공유 코드가 앱 파일에 의존하게 된다.
 *
 * `status === 0` 은 상태 코드가 없다는 뜻이다(네트워크 단절·타임아웃·중단). */
export interface RemakeErrorLike {
  status: number;
  code?: string | null;
}

/** 버튼 줄을 어떻게 그리는가. **갈래마다 화면이 `if` 를 새로 쓰면 이 표가 두 벌이 된다.**
 *
 * 두 종류뿐인 것은 줄여 쓴 것이 아니라 갈래가 실제로 둘이기 때문이다 — **다시 걸 것이
 * 남았는가**로 갈린다. */
export type RemakeActionRow =
  /** ① 전용. 이미 만들어졌으니 다시 걸 것이 없다. 주 버튼 하나로 닫는다. */
  | { kind: "DISMISS"; label: string }
  /** ②③④⑤. 대기 시트와 같은 줄(`다시 만들기` + `그만두기`).
   *  `disabled` 면 `다시 만들기` 를 비활성으로 그리고 `disabledReason` 을 아래에 붙인다
   *  (`apps/mobile/src/components/button.tsx` 의 `disabledReason` 이 그 자리다). */
  | {
      kind: "REMAKE";
      disabled: boolean;
      disabledReason: string | null;
      /** 둘째 버튼 라벨. 없으면 화면의 기본(`그만두기`)이다. 다시 걸 수 없는 갈래(받기 기간)는
       *  `닫기` 를 준다. 그만둘 것이 없는데 `그만두기` 라고 쓰지 않는다(PRD `7.6.14c` ③). */
      closeLabel?: string;
    };

/** 시트가 그릴 것 한 벌. `LimitBanner` 한 줄과 버튼 줄이 전부다. */
export interface RemakeOutcomeMessage {
  /** 어느 갈래로 떨어졌는지. 화면이 다시 판정하지 않게 함께 준다. */
  outcome: RemakeOutcome;
  /** `LimitBanner` 의 `icon`. 실패 다섯은 전부 `!` 다. */
  icon: string;
  /** 배너 문구. 셀러가 읽는 말 그대로다. */
  text: string;
  actions: RemakeActionRow;
}

/** HTTP 상태 + `code` → 갈래.
 *
 * **앱과 웹이 각자 `if (status === 409 && code === ...)` 를 쓰면 같은 실패가 두 화면에서
 * 다른 말이 된다.** 판정은 여기 한 곳이다(`job-rules.ts` 머리말과 같은 규율).
 *
 * ⚠️ **실패했을 때만 부른다.** 200 은 갈래가 아니다 — 성공은 `JobResponse` 를 받는 쪽이
 * 처리한다. 2xx 를 넣으면 모르는 응답 취급(④)을 받는다.
 *
 * **모르는 응답은 ④(`NO_ANSWER`)로 떨어진다.** 500 처럼 우리가 열거하지 않은 것은 호출
 * 전이었는지 후였는지 알 수 없고, **④ 만이 돈에 대해 단정하지 않는 갈래**다. ③ 으로
 * 떨어뜨리면 돈이 나갔을 수도 있는 응답에 `금액은 들지 않았습니다` 를 쓰게 된다. */
export function remakeOutcomeOf(error: RemakeErrorLike): RemakeOutcome {
  // 상태 코드가 없다. 요청이 나갔는지도 모른다.
  if (error.status === 0) return "NO_ANSWER";

  // 부른 뒤다. 코드가 무엇이든(`PROVIDER_REJECTED`·`PROVIDER_FAILED`·`SOURCE_MISSING`)
  // 돈은 나갔다. 이 한 줄이 409 와 갈리는 지점 전부다.
  if (error.status === 502) return "FAILED_AFTER_CALL";

  if (error.status === 409) {
    if (error.code === "STALE_CALL_COUNT") return "ALREADY_REMADE";
    if (error.code === "ALREADY_PROCESSING") return "JOB_BUSY";
    // `JOB_DISCARDED` · `IMAGE_NOT_DONE` · `SOURCE_MISSING`(부르기 전 판) · 코드 없는 409.
    // (`JOB_DISCARDED` 는 갈래는 ③ 그대로이고 문장만 `remakeOutcomeMessage` 가 따로 고른다.)
    return "BLOCKED_BEFORE_CALL";
  }

  // ⚠️ **`400`·`404` 는 화면이 버튼을 그리기 전에 `canRemakeImage` 로 이미 막는 갈래다**
  // (`IMAGE_NOT_DONE`·`JOB_DISCARDED` 와 함께). 그래도 오면 ③ 으로 둔다 — ④ 로 보내면
  // "금액이 들었는지는 위 횟수 줄로 확인하세요"가 되는데, **이 넷은 돈이 안 나간 것이
  // 사실이라** 굳이 셀러에게 확인시킬 이유가 없다.
  //
  // `401` 도 여기다. 다만 앱에서는 여기까지 거의 오지 않는다 — `apps/mobile/src/lib/api.ts`
  // 의 `notifyUnauthorized` 가 먼저 받아 로그인으로 보낸다. 그래도 닿았을 때 `지금은 다시
  // 만들 수 없어요` 가 **원인은 덜 정확해도 돈에 대해서는 참**이라 이 줄이 더 안전하다.
  if (
    error.status === 400 ||
    error.status === 401 ||
    error.status === 403 ||
    error.status === 404 ||
    error.status === 503
  ) {
    return "BLOCKED_BEFORE_CALL";
  }

  return "NO_ANSWER";
}

/** 갈래 → 문구·버튼 모양. 판정까지 함께 한다.
 *
 * **판정과 문구를 한 번에 부르게 한 것은 의도다.** 둘로 나눠 `(outcome, code)` 를 받으면
 * 호출하는 쪽이 **다른 오류에서 뽑은 `code`** 를 넘길 수 있고(예: `image.errorCode`),
 * 그러면 ⑤ 가 이번 실패와 상관없는 말을 한다. 갈래만 필요하면 `remakeOutcomeOf` 를 쓴다.
 *
 * ⛔ **여기 담긴 문자열을 화면에서 고쳐 쓰지 마라.** 정본은 `design-manager` 이고
 * (2026-09-13 확정) 고칠 일이 생기면 그쪽에 되묻는다. */
export function remakeOutcomeMessage(
  error: RemakeErrorLike,
  options: {
    revive?: boolean;
    /** 실제로 눌러서 돌아온 거절인가. 기본 `true`. **열 때 서버 판정으로 미리 세우는 모양이면 `false`**
     *  (작업 보기 `resultReceive` 가 `CLOSED`). 그때는 과거형 돈 문장(「금액은 들지 않았습니다」)을
     *  붙이지 않는다. 누른 적이 없다(PRD `7.6.14c` ③, 2026-10-13 정정). */
    pressed?: boolean;
  } = {},
): RemakeOutcomeMessage {
  const outcome = remakeOutcomeOf(error);

  // ⭐ **③ 안에서 코드로 가르는 둘** (PRD `7.6.14c` ③ ④. 2026-10-13 `design-manager`).
  //    둘 다 공급자를 부르기 전이라 갈래는 ③(`BLOCKED_BEFORE_CALL`) 그대로이고 문장만 다르다.
  //    ③ 의 기본 문장 「서비스 쪽 준비가 끝나지 않았습니다」는 이 둘에서 거짓 원인이다.
  if (outcome === "BLOCKED_BEFORE_CALL" && error.code === RESULT_RECEIVE_EXPIRED_CODE) {
    // 받기 기간 때문에 만들어도 받을 수 없다. 기다려도 안 풀리므로 버튼을 끄고 사유를 붙인다.
    // `금액은 들지 않았습니다` 는 만든 장(`A-25`)에만, 그리고 **실제로 눌러 409 가 돌아온 뒤에만** 붙인다.
    // 되살리기는 원래 새로 나가는 돈이 없다(PRD `7.6.14c` ③).
    const copy = RESULT_MAKE_EXPIRED_COPY;
    const withMoney = !options.revive && options.pressed !== false;
    return {
      outcome,
      icon: "!",
      text: (withMoney ? [copy.head, copy.reason, copy.money] : [copy.head, copy.reason]).join(" "),
      actions: {
        kind: "REMAKE",
        disabled: true,
        disabledReason: copy.disabledReason,
        closeLabel: "닫기",
      },
    };
  }
  if (outcome === "BLOCKED_BEFORE_CALL" && error.code === JOB_DISCARDED_CODE) {
    // ⭐ 원본이 없는 작업(`DISCARDED`). 편집 거절(웹 `edit-messages.ts` · 앱 `job-queries.ts`)과
    //    같은 첫 줄을 쓴다(2026-11-01 사용자 결정). ③ 의 기본 문장 「서비스 쪽 준비가 끝나지
    //    않았습니다」는 여기서 거짓 원인이고, 기다려도 풀리지 않으므로 버튼을 끄고 `닫기` 를 준다.
    //    돈 문장은 받기 기간 갈래와 같은 규칙이다. 만든 장(`A-25`)에서 **실제로 눌러 409 가
    //    돌아온 뒤에만** 붙인다. 되살리기는 원래 새로 나가는 돈이 없다.
    //    같은 사실이라 받기 기간 갈래의 돈 문장 글자를 빌린다(한 사실을 두 글자로 쓰지 않는다).
    const copy = REVIVE_BLOCKED_COPY.discarded;
    const withMoney = !options.revive && options.pressed !== false;
    return {
      outcome,
      icon: "!",
      text: withMoney ? `${copy.lines[0]} ${RESULT_MAKE_EXPIRED_COPY.money}` : copy.lines[0],
      actions: {
        kind: "REMAKE",
        disabled: true,
        disabledReason: copy.disabledReason,
        closeLabel: "닫기",
      },
    };
  }
  if (outcome === "BLOCKED_BEFORE_CALL" && error.code === IMAGE_NOT_REQUEUED_CODE) {
    // 되살리기만 내는 코드다. 이미 다시 만드는 중일 수 있어 다시 걸 버튼을 두지 않는다.
    // 화면은 실패하면 작업을 다시 읽는다(앱 `onError` · 웹 `W-21` 의 무효화).
    return {
      outcome,
      icon: "!",
      text: IMAGE_NOT_REQUEUED_TEXT,
      actions: { kind: "DISMISS", label: "닫기" },
    };
  }

  switch (outcome) {
    // ① 돈이 한 번만 들었다는 사실을 먼저 말한다. 이 갈래에서 셀러가 가장 두려워하는 것이
    //    "두 번 나갔나"다. 다시 걸 것이 없으니 버튼도 하나다.
    case "ALREADY_REMADE":
      return {
        outcome,
        icon: "!",
        text: "이미 다시 만들어졌습니다. 다른 기기에서 먼저 눌렀거나 같은 요청이 두 번 닿았습니다. 금액은 한 번만 듭니다.",
        actions: { kind: "DISMISS", label: "닫기" },
      };

    // ② 끝나면 다시 누를 수 있으니 버튼은 대기와 같다.
    case "JOB_BUSY":
      return {
        outcome,
        icon: "!",
        text: "지금 이 작업의 다른 장을 만들고 있어요. 그 장이 끝나면 다시 눌러 주세요. 아직 금액이 들지 않았습니다.",
        actions: { kind: "REMAKE", disabled: false, disabledReason: null },
      };

    // ③ `금액은 들지 않았습니다` 를 단정할 수 있는 갈래. 409·503 이 그것을 보증한다.
    case "BLOCKED_BEFORE_CALL":
      return {
        outcome,
        icon: "!",
        text: "지금은 다시 만들 수 없어요. 서비스 쪽 준비가 끝나지 않았습니다. 금액은 들지 않았습니다.",
        actions: { kind: "REMAKE", disabled: false, disabledReason: null },
      };

    // ④ **돈에 대해 단정하지 않는 유일한 갈래.** 우리가 모르는 것을 안다고 말하지 않고,
    //    셀러가 직접 확인할 자리(횟수 줄)를 가리킨다. 그 줄이 `jobProviderRecallCount` 다.
    case "NO_ANSWER":
      return {
        outcome,
        icon: "!",
        text: "다시 만들지 못했어요. 연결이 끊겼을 수 있습니다. 금액이 들었는지는 위 횟수 줄로 확인하세요.",
        actions: { kind: "REMAKE", disabled: false, disabledReason: null },
      };

    // ⑤ 부른 뒤다. 사유는 저쪽 표에서 `short` 만 빌려 오고, **돈 문장은 이쪽이 붙인다.**
    //   `detail` 을 안 쓰는 이유가 이것이다 — 그쪽은 "다시 시도해 주세요"로 끝난다.
    //
    // ⚠️ **옛 문장은 `이번 금액은 돌아갑니다` 였다. 지킬 수 없는 약속이었다**
    // (2026-09-13 이중 코드리뷰). 근거는 이 파일이 아니라 12.5 에 있다: **다시 만들기가
    // 실패해도 장은 `DONE` 인 채로 남는다.** 그래서 그 장은 `failedImages` 에도
    // `PARTIAL_FAILURE` 에도 들지 않고, **실패한 장 값을 돌려주는 경로가 이 실패를 볼 수
    // 없다.** 화면이 돌아간다고 말하는데 돌려줄 코드가 그 사건을 모르는 상태였다.
    //
    // **뒤집어서 `돌아가지 않습니다` 라고 쓰지도 않았다.** 그것도 지금은 거짓이다.
    // 다시 만들기 실패분의 처리는 **아직 정해지지 않았다**(환불 실행 방식 미결 · 부분 실패
    // 차액 정책 미결. `docs/DATA_DICTIONARY.md` 8장). 정해지지 않은 것을 화면이 어느
    // 한쪽으로 정하면, 정하는 날 셀러에게 한 말을 되돌려야 한다.
    //
    // **그래서 지금 참인 것만 적는다.** ⓐ 결과는 그대로다(12.5 가 보증한다).
    // ⓑ 결제가 아직 붙지 않아 금액이 나가지 않았다(`A-26` 미구현. 대기 갈래가 이미 같은
    // 사실을 같은 낱말로 적는다). ⓒ 붙은 뒤의 처리는 미정이다. **ⓒ 를 빼지 않는 이유**는
    // ⓑ 만 적으면 셀러가 "이 실패는 원래 공짜"로 읽기 때문이다.
    //
    // ⚠️ **결제가 붙는 날 이 문장은 반드시 다시 쓴다.** ⓑ 가 그날 거짓이 된다.
    // 문구 정본은 `design-manager`(PRD `7.6.8`)이고, 그 표는 아직 옛 문장을 들고 있다.
    case "FAILED_AFTER_CALL":
    default: {
      const failure = failureMessage(error.code);
      return {
        outcome: "FAILED_AFTER_CALL",
        icon: "!",
        text: `${failure.short}. 지금 결과는 그대로 있고, 결제가 아직 붙지 않아 이번 금액은 나가지 않았습니다. 결제가 붙은 뒤 이 금액을 어떻게 할지는 아직 정해지지 않았습니다.`,
        // 같은 사진으로 다시 만들어도 결과가 같은 실패(`PROVIDER_REJECTED`·`SOURCE_MISSING`)
        // 에서 버튼을 열어 두면 셀러가 같은 돈을 또 쓴다. `failureMessage` 의 기본값이
        // `retryable: false` 인 것도 같은 이유라, **모르는 코드는 잠기는 쪽으로 떨어진다.**
        actions: failure.retryable
          ? { kind: "REMAKE", disabled: false, disabledReason: null }
          : {
              kind: "REMAKE",
              disabled: true,
              disabledReason: "같은 사진으로 다시 만들어도 결과가 같습니다",
            },
      };
    }
  }
}

// ── 되살리기 시트·패널의 글자 (앱 `A-25b` · `A-25c`, 웹 `W-21`) ─────────────────────────

/** 같은 문장을 어느 화면이 그리는가. **두 화면이 다르게 말하는 자리가 하나 있다**
 *  (`REVIVE_SHEET_COPY.retakeHint`). PC 에는 카메라가 없어 웹은 `다시 올리는 편` 이다(목업 `W-21`). */
export type ReviveSurface = "APP" | "WEB";

/** 못 만든 장 다시 만들기(`A-25b` · 웹 `W-21` 의 `RevivePanel`)가 하는 말.
 *
 * ## 왜 여기 있나 (2026-10-13)
 *
 * 앱 시트(`apps/mobile/src/features/job/retry-sheet.tsx`)와 웹 패널
 * (`apps/web/src/screens/stages/Uploading.tsx`)이 같은 문장을 **각자 글자로** 들고 있었다.
 * 시험(`scripts/web-revive.test.mjs` ④)이 두 사본이 같은지 맞대고 있었지만, 그것은 갈리면
 * 알아채는 장치일 뿐 갈릴 자리를 없애지는 못한다. 그래서 글자를 여기 한 벌로 올렸다.
 * **옮기면서 글자는 하나도 바꾸지 않았다.** 정본은 그대로 `design-manager`(PRD `7.6.8` ·
 * `7.6.8a`, 목업 `A-25b` · `A-25c` · `W-21`)이고, 여기서 글자를 정하지 않는다.
 *
 * ⛔ **화면에서 이 문장을 다시 손으로 적지 마라.** 한쪽만 손글자로 돌아오면 ④ 가 막는다.
 *
 * ⚠️ 만든 장 다시 만들기(`A-25`)의 본문과 대기 배너(`결제가 아직 붙지 않아 …`)는 여기 없다.
 * 앱에만 있는 갈래라(웹은 그 동작을 열지 않는다) 화면이 들고 있다. 진행 배너와 진행 버튼
 * 라벨은 `A-25` 도 같은 글자라 화면이 이 값을 함께 쓴다. */
export const REVIVE_SHEET_COPY = {
  /** 제목 뒤쪽. 앞은 `{n}번 사진` 이다(`remakeSheetTitle`). */
  titleTail: "한 장을 다시 만들까요?",
  /** 본문. 목업 `A-25b` 글자 그대로다. */
  body: "이 장은 결과가 없어 받는 금액이 없습니다. 다시 만들어도 더 내실 금액이 없습니다. 같은 사진으로 다시 해도 결과가 같을 수 있습니다. 지금 비어 있는 이 자리가 새 결과로 채워집니다.",
  /** 본문 꼬리. **`PROVIDER_REJECTED` 에만 붙는다**(`reviveSheetBody`). 촬영을 가리키는 알려진
   *  원인은 그 코드뿐이다(PRD `7.6.13b`). 두 화면이 갈리는 유일한 자리다. */
  retakeHint: {
    APP: "상품과 바닥 색이 비슷했다면 다시 찍는 편이 낫습니다.",
    WEB: "상품과 바닥 색이 비슷했다면 다시 올리는 편이 낫습니다.",
  },
  /** 횟수 줄 라벨(`jobProviderRecallCount`). */
  remakeCountLabel: "이번 작업에서 더 만든 횟수",
  /** 요청이 나가 있는 동안의 주 버튼 라벨. */
  pendingLabel: "다시 만드는 중",
  /** 요청이 나가 있는 동안의 `↓` 배너. */
  pendingBanner: "다시 만들고 있습니다. 닫아도 계속 만듭니다.",
  /** 누르기 전의 `₩` 배너 두 문장. 웹은 두 줄로 나눠 그리고 앱은 한 문단으로 잇는다. */
  waitingBanner: [
    "이 장 금액은 그대로 이 장에 쓰입니다.",
    "돌아가지도, 새로 나가지도 않습니다. 새 결제를 묻지 않는 이유입니다.",
  ],
  /** 다시 걸 수 있는 실패 뒤 배너의 꼬리. 앞은 그 장의 실패 사유 `short` 다(`reviveFailedText`). */
  failedTail:
    "이 자리는 그대로 비어 있고 이번에도 받는 금액이 없습니다. 이 장 금액은 다시 만들지 않으시면 돌아갑니다.",
} as const;

/** `{n}번 사진 한 장을 다시 만들까요?`. 앱은 만든 장(`A-25`)도 같은 제목을 쓴다. */
export function remakeSheetTitle(imageName: string): string {
  return `${imageName} ${REVIVE_SHEET_COPY.titleTail}`;
}

/** `A-25b` 본문. `PROVIDER_REJECTED` 면 화면 갈래의 꼬리를 붙인다.
 *
 * ⛔ **다른 코드에 꼬리를 붙이지 않는다.** 색 대비와 무관한 실패에 색 이야기를 하면 셀러가
 * 멀쩡한 사진을 다시 찍는다(PRD `7.6.13b`). */
export function reviveSheetBody(
  surface: ReviveSurface,
  errorCode: JobFailureCode | null | undefined,
): string {
  return errorCode === "PROVIDER_REJECTED"
    ? `${REVIVE_SHEET_COPY.body} ${REVIVE_SHEET_COPY.retakeHint[surface]}`
    : REVIVE_SHEET_COPY.body;
}

/** 다시 걸 수 있는 실패 뒤 `A-25b` 배너. 사유는 **그 장의** 실패 코드에서 온다.
 *
 * ⛔ `remakeOutcomeMessage` 의 다섯째 갈래 문장을 쓰지 않는 이유: 그쪽은 `지금 결과는 그대로
 * 있고` 인데 못 만든 장에는 그대로 있을 결과가 없다(PRD `7.6.8a`). `이번에도` 는 이 갈래에서만
 * 참이다(2026-09-21 `design-manager`). */
export function reviveFailedText(errorCode: JobFailureCode | null | undefined): string {
  return `${failureMessage(errorCode).short}. ${REVIVE_SHEET_COPY.failedTail}`;
}

/** 지금은 다시 만들 수 없는 못 만든 장(`A-25c` · 웹 `W-21` 의 `BlockedPanel`)이 하는 말.
 *
 * 갈래 넷의 본문은 두 줄이다. 앱은 `\n` 으로, 웹은 공백으로 잇는다(`lines.join`).
 * `RETENTION_ENDED_COPY`(`failure-messages.ts`, 원본 보관이 끝난 장)와 같은 꼴로 두었다.
 * 그 갈래는 그 파일이 정본이라 여기 다시 적지 않는다. */
export const REVIVE_BLOCKED_COPY = {
  /** 제목 뒤쪽. 앞은 `{n}번 사진` 과 `이 장은` / `지금은` 이다(`reviveBlockedTitle`). */
  titleTail: "다시 만들 수 없어요",
  /** 처리할 수 있는 크기를 넘은 사진(`SOURCE_TOO_LARGE`). */
  tooLarge: {
    lines: [
      "이 사진은 처리할 수 있는 크기를 넘어 다시 보낼 수 없습니다.",
      "더 작게 찍거나 줄여서 새로 올려 주세요.",
    ],
    disabledReason: "이 사진은 보낼 수 없어 누를 수 없습니다.",
  },
  /** 원본이 없는 **작업**(`JOB_DISCARDED`). 그 장 하나가 아니라 작업의 밑천이 통째로 없다
   *  (`job-rules.ts` `recallBlockOf`). 첫 줄은 편집 거절과 같은 글자다(2026-11-01 사용자 결정).
   *  둘째 줄은 `sourceGone` 과 같다. 셀러가 할 일(다시 찍어 새로 올리기)이 같기 때문이다.
   *  누른 뒤 409 가 돌아온 다시 만들기 · 되살리기도 이 첫 줄과 사유를 쓴다(`remakeOutcomeMessage`). */
  discarded: {
    lines: [
      "이 작업의 원본 사진이 없어져 더 진행할 수 없습니다.",
      "같은 상품을 다시 찍어 새로 올려 주세요.",
    ],
    disabledReason: "원본 사진이 없어 누를 수 없습니다.",
  },
  /** 보관된 원본이 없는 사진. */
  sourceGone: {
    lines: [
      "보관된 원본이 없어 이 사진을 다시 보낼 수 없습니다.",
      "같은 상품을 다시 찍어 새로 올려 주세요.",
    ],
    disabledReason: "보낼 원본이 없어 누를 수 없습니다.",
  },
  /** 만드는 일이 막혀 있다. 일시적이다. */
  blocked: {
    lines: [
      "만드는 일이 아직 막혀 있어 지금 다시 만들어도 같은 결과가 됩니다.",
      "풀리면 이 자리에서 바로 다시 만들 수 있습니다.",
    ],
    disabledReason: "만드는 일이 막혀 있는 동안에는 누를 수 없습니다.",
  },
  /** `!` 배너. 보낼 사진이 없는 갈래(`TERMINAL`)와 막힌 갈래가 다르다. */
  terminalBanner: "이 장은 결과가 없어 받는 금액이 없습니다. 이 장 금액은 다시 만들지 않으시면 돌아갑니다.",
  waitingBanner:
    "기다리는 동안 더 내실 것도, 잃으실 것도 없습니다. 이 장 금액은 그대로 있습니다. 다시 만들면 그 금액으로 만들고, 다시 만들지 않으시면 돌아갑니다.",
} as const;

/** `{n}번 사진 이 장은 다시 만들 수 없어요` / `{n}번 사진 지금은 다시 만들 수 없어요`.
 *  `지금은` 은 곧 바뀐다는 말이라 보낼 사진이 없는 갈래에 쓰면 거짓이다. */
export function reviveBlockedTitle(imageName: string, terminal: boolean): string {
  return `${imageName} ${terminal ? "이 장은" : "지금은"} ${REVIVE_BLOCKED_COPY.titleTail}`;
}
